/**
 * Add a certification, or record a later attempt at one.
 *
 * ── One modal for both, because they are the same fields ──────────────────────────────────
 *
 * Adding a completed certification and recording a retake capture identical facts: a result, its
 * dates, and a document. The only differences are that a retake already knows the name and
 * provider, and that it APPENDS rather than creates.
 *
 * `retakeOf` is what switches between them. Two components would have meant two copies of the
 * mandatory-document rule and two chances for one of them to loosen it.
 *
 * ── What a retake must not do ─────────────────────────────────────────────────────────────
 *
 * It must not lose the failed attempt. That is enforced in CertificationService.recordRetake,
 * which appends to `attempts` and never rewrites an entry — this component shows the earlier
 * attempts while the form is open so it is visible that they survive.
 */

import React, { useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { canonicalizeEmail } from '../../services/identity';
import {
  Certification,
  CertificationDraft,
  CertificationResult,
  validateCertificationDraft,
} from '../../types/certification';
import { CertificationService } from '../../services/certificationService';
import CertificationForm from './CertificationForm';
import './Certifications.css';

interface Props {
  /** Present when recording another attempt at an existing certification. */
  retakeOf?: Certification;
  onClose: () => void;
  onSaved: () => void;
}

const formatDate = (iso?: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
  if (!match) return '—';
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${Number(match[3])} ${MONTHS[Number(match[2]) - 1]} ${match[1]}`;
};

const AddCertificationModal: React.FC<Props> = ({ retakeOf, onClose, onSaved }) => {
  const { user } = useAuth();
  const myEmail = canonicalizeEmail(user?.email);

  /*
   * A certification is always recorded against the SIGNED-IN employee.
   *
   * There is no employee picker, for anybody — not even an administrator. This page is somebody
   * maintaining their own qualification history, and an admin recording a certification "on
   * behalf of" a colleague produces a record whose provenance nobody can later reconstruct: the
   * employee did not enter it, did not upload the certificate, and may not know it exists.
   *
   * The owner is not merely defaulted here either. The createCertification resolver takes
   * employeeEmail from the token's email claim, so the request cannot even express somebody
   * else's record — removing the control is the visible half of a rule the server enforces.
   */
  const [draft, setDraft] = useState<Partial<CertificationDraft>>(
    retakeOf
      ? { name: retakeOf.name, provider: retakeOf.provider, result: CertificationResult.PASSED }
      : {}
  );
  const [errors, setErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState('');

  /**
   * Whose record this is, and therefore whose folder the document goes into.
   *
   * A retake belongs to the certification being retaken; everything else belongs to the person
   * signed in. There is no third case, which is the point.
   */
  const ownerEmail = useMemo(
    () => (retakeOf ? canonicalizeEmail(retakeOf.employeeEmail) : myEmail),
    [retakeOf, myEmail]
  );

  const submit = async () => {
    setFailure('');
    const problems = validateCertificationDraft({
      name: retakeOf?.name || draft.name,
      provider: retakeOf?.provider || draft.provider,
      result: draft.result,
      examDate: draft.examDate,
      resultDate: draft.resultDate,
      expiryDate: draft.expiryDate,
      documentKey: draft.documentKey,
    } as CertificationDraft);

    if (problems.length > 0) {
      setErrors(problems);
      return;
    }
    setErrors([]);
    setSaving(true);

    try {
      if (retakeOf) {
        await CertificationService.recordRetake(
          retakeOf,
          {
            result: draft.result!,
            examDate: draft.examDate,
            resultDate: draft.resultDate!,
            expiryDate: draft.expiryDate,
            documentKey: draft.documentKey,
            documentName: draft.documentName,
          },
          myEmail
        );
      } else {
        await CertificationService.create(
          draft as CertificationDraft,
          { email: myEmail, name: user?.name, employeeId: (user as any)?.employeeId },
          // No canRecordForOthers: this modal records for the signed-in employee only.
          {}
        );
      }
      onSaved();
    } catch (error: any) {
      setFailure(error?.message || 'The certification could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cert-modal" role="dialog" aria-modal="true" aria-label={retakeOf ? 'Record attempt' : 'Add certification'}>
      <div className="cert-modal__panel">
        <div className="cert-modal__head">
          <h2>{retakeOf ? 'Record another attempt' : 'Add Certification'}</h2>
          <button type="button" className="cert-modal__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="cert-modal__body">
          {retakeOf ? (
            <>
              <p className="cert-modal__lead">
                <strong>{retakeOf.name}</strong> — {retakeOf.provider}
              </p>
              {/*
                * The earlier attempts, shown while the new one is being entered.
                *
                * The requirement is that history is not lost. Showing it here is how somebody
                * can see that before they save, rather than having to trust it and check after.
                */}
              <div className="cert-modal__history">
                <span className="cert-form__label">Previous attempts</span>
                <ul>
                  {retakeOf.attempts.map((attempt) => (
                    <li key={attempt.id}>
                      <span
                        className={`certs__pill certs__pill--${
                          attempt.result === CertificationResult.PASSED ? 'passed' : 'failed'
                        }`}
                      >
                        {attempt.result}
                      </span>
                      <span>{formatDate(attempt.resultDate)}</span>
                      {attempt.retakeDeadline && (
                        <span className="certs__muted">
                          retake by {formatDate(attempt.retakeDeadline)}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
                <small className="cert-form__hint">
                  These are kept. A new attempt is added alongside them, not over them.
                </small>
              </div>
            </>
          ) : null}

          <CertificationForm
            value={draft}
            onChange={setDraft}
            ownerEmail={ownerEmail}
            errors={errors}
            disabled={saving}
            mode={retakeOf ? 'attempt' : 'full'}
          />

          {failure && (
            <div className="certs__notice certs__notice--error" role="alert">
              {failure}
            </div>
          )}
        </div>

        <div className="cert-modal__actions">
          <button type="button" className="certs__btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="certs__btn certs__btn--primary"
            onClick={submit}
            disabled={saving}
          >
            {saving ? 'Saving…' : retakeOf ? 'Save attempt' : 'Add certification'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddCertificationModal;
